import { isNotDefined } from '@sgme/fp';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import { App } from '@/App/App.tsx';
import { SgwtBootstrap } from '@/components/SgwtBootstrap/SgwtBootstrap.tsx';
import { getConfig } from '@/config/config.ts';
import { registerBlotterListeners } from '@/store/blotter/registerBlotterListeners.ts';
import { registerChangeClientListenersForBulkTrade } from '@/store/bulkTrade/listeners/change-client-listeners.ts';
import { registerRfsBulkListeners } from '@/store/bulkTrade/listeners/rfs-listeners.ts';
import { registerCashEspListeners } from '@/store/cash/listeners/esp-listener.ts';
import { registerCashListeners } from '@/store/cash/listeners/registerCashListeners.ts';
import { registerEspListeners } from '@/store/esp/listener.ts';
import { registerFlexiForwardListeners } from '@/store/flexi-forward/listeners/registerFlexiForwardListeners.ts';
import { registerEspForwardAccumulatorListeners } from '@/store/forwardAccumulator/listeners/esp-listener.ts';
import { registerForwardAccumulatorListeners } from '@/store/forwardAccumulator/listeners/registerForwardAccumulatorListeners.ts';
import { registerGrowlListeners } from '@/store/growl/listeners.ts';
import { startAppListening } from '@/store/listenerMiddleware.ts';
import { registerEspOptionListeners } from '@/store/option/listeners/esp-listener.ts';
import { registerOptionListeners } from '@/store/option/listeners/registerOptionListeners.ts';
import { registerEspOrderListeners } from '@/store/orders/listeners/registerEspOrderListeners.ts';
import { registerOrderListeners } from '@/store/orders/listeners/registerOrderListeners.ts';
import { registerSharedWorkspaceListeners } from '@/store/sharedWorkspace/listeners.ts';
import { store } from '@/store/store.ts';
import { registerSwapListeners } from '@/store/swap/listeners/registerSwapListeners.ts';
import { registerTabsEffects, registerUpdateTileProductTypeListener } from '@/store/tabs/effects.ts';
import { registerEspTargetAccumulatorListeners } from '@/store/targetAccumulator/listeners/esp-listener.ts';
import { registerTargetAccumulatorListeners } from '@/store/targetAccumulator/listeners/registerTargetAccumulatorListeners.ts';
import { registerWorkspaceListeners } from '@/store/workspace/listeners.ts';
import { registerTradeCaptureListeners } from '@/store/workspace/trade-capture.effects';

import './App.css';

const renderApp = () => {
  const rootElement = document.getElementById('root');
  if (isNotDefined(rootElement)) {
    console.error('Root element not found');
    return;
  }

  const root = createRoot(rootElement);

  const config = getConfig().app;

  // Core listeners - always loaded
  registerWorkspaceListeners(startAppListening);
  registerTabsEffects(startAppListening);

  // Product listeners
  registerCashListeners(startAppListening);
  registerSwapListeners(startAppListening);
  registerOptionListeners(startAppListening);
  registerTargetAccumulatorListeners(startAppListening);
  registerForwardAccumulatorListeners(startAppListening);
  registerFlexiForwardListeners(startAppListening);
  registerOrderListeners(startAppListening);

  // Tile listeners
  registerUpdateTileProductTypeListener(startAppListening);
  registerSharedWorkspaceListeners(startAppListening);
  registerTradeCaptureListeners(startAppListening);
  registerBlotterListeners(startAppListening);
  registerGrowlListeners(startAppListening);

  // ESP listeners
  registerEspListeners(startAppListening);
  registerCashEspListeners(startAppListening);
  registerEspOptionListeners(startAppListening);
  registerEspTargetAccumulatorListeners(startAppListening);
  registerEspForwardAccumulatorListeners(startAppListening);
  registerEspOrderListeners(startAppListening);

  // bulk trade
  registerChangeClientListenersForBulkTrade(startAppListening);
  registerRfsBulkListeners(startAppListening);

  window.addEventListener('beforeunload', async () => {
    const { cancelAllOptionsRfsThunk } = await import('@/store/option/thunks/thunk.ts');
    await store.dispatch(cancelAllOptionsRfsThunk());
  });

  root.render(
    <StrictMode>
      <Provider store={store}>
        <SgwtBootstrap
          infrastructure="azure"
          sgConnect={{
            clientId: config.sgconnect.client_id, // FXUSer
            endpoint: config.sgconnect.authorization_endpoint,
            redirectUri: config.sgconnect.redirect_uri,
            scope: config.sgconnect.scope,
            acrValues: config.sgconnect.acr_values,
          }}
          sgSplashScreen={{
            applicationId: import.meta.env.VITE_APP_ID,
          }}
          sgAnalytics={{
            siteId: config.matomo.siteId,
            url: config.matomo.url,
          }}
        >
          <App />
        </SgwtBootstrap>
      </Provider>
    </StrictMode>,
  );
};

renderApp();
